/* WhyInvisique — full marketing page rebuilt 2026-02-24 to match the
 * homepage's editorial polish.
 *
 * Layout (top to bottom):
 *   1. HERO       — asymmetric 2-col: left copy + dual CTAs · right lifestyle
 *                   photo with glassmorphic testimonial chip + IDA Certified badge
 *   2. EVIDENCE   — 4-stat band on a deep teal gradient (kept from v1 — works)
 *   3. PILLARS    — 4 alternating zig-zag image-left/right storytelling blocks
 *   4. SPOTLIGHT  — full-width pull-quote from Dr. Ritu Malhotra with portrait
 *   5. COMPARISON — 7-row table; Invisique column gets a "Recommended" ribbon,
 *                   glow shadow, scale-up; competitor columns recede
 *   6. TEAM       — staggered 3-card collage with hover lift + glassmorphic strip
 *   7. CERTS      — outlined cards in a horizontal row (not a flat text strip)
 *   8. CTA        — dual button (Find a Doctor + Talk to our team)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
    Sparkle, Atom, Flask, ArrowRight, CheckCircle, XCircle, Factory, Cpu,
    Certificate, Quotes, ShieldStar, Feather, MapPin,
    Pulse, Shield, ArrowClockwise, Diamond, Stack,
    MagnifyingGlass, UsersThree, Note, Clock,
} from "@phosphor-icons/react";


// -----------------------------------------------------------------------------
// Content data (kept from v1 — conversion-critical, do not drop a field)
// -----------------------------------------------------------------------------

const HERO_IMAGE = "/marketing/why-invisique-hero.png";
const MATERIAL_HERO_IMAGE = "/marketing/why-invisique-material.png";

/* Compact at-a-glance pillar summary — heavy editorial zigzag retired
   2026-02-24 (user feedback: too bulky). Each pillar is now a small card.
   The deep-dive content for material science moved to the dedicated
   `MaterialScience` section below, which is the strongest credibility
   beat on the page. */
const PILLARS = [
    {
        icon: Atom,
        num: "01",
        title: "Built for Indian anatomy",
        blurb: "SmartForce™ AI trained on 2M+ Indian cases. Optimized for narrower arches, distinct Class II/III ratios, and Indian dental morphology.",
        testid: "pillar-biomechanics",
    },
    {
        icon: Flask,
        num: "02",
        title: "Premium tri-layer material",
        blurb: "World-class clear aligner sheet — dual rigid outer shells bonded to a soft elastomeric core. Gentler day one, steady force, longer life.",
        testid: "pillar-material",
    },
    {
        icon: Cpu,
        num: "03",
        title: "AI + clinician dual-pass",
        blurb: "Every plan reviewed by ClearPath™ AI and a certified orthodontist before production. 40% fewer mid-treatment revisions.",
        testid: "pillar-ai",
    },
    {
        icon: Factory,
        num: "04",
        title: "Engineered in Bengaluru",
        blurb: "ISO 13485 facility · CDSCO-registered medical device · pan-India 3-day delivery, no customs delays.",
        testid: "pillar-india",
    },
];

/* Six science beats for the dedicated material section. Copy preserved
   from the user's brief verbatim. (6th "Multi-layer engineering" added
   2026-02-25 to balance the lg:grid-cols-3 grid as a clean 2×3.) */
const MATERIAL_FEATURES = [
    {
        icon: Feather,
        title: "Gentler on Day One",
        stat: "40%",
        statLabel: "lower initial force",
        desc: "Up to 40% lower initial force than standard monolayer materials, so the first few hours of a new tray feel noticeably more comfortable.",
    },
    {
        icon: Pulse,
        title: "Consistent force, all day",
        stat: "150%",
        statLabel: "better force retention",
        desc: "Up to 150% better force retention — your teeth receive steady, reliable pressure throughout the wear period, exactly as your plan intends.",
    },
    {
        icon: Shield,
        title: "Built to last",
        stat: "Hi-tear",
        statLabel: "crack resistant",
        desc: "Significantly higher tear strength and crack resistance than conventional aligner materials — no unexpected breaks or distortions mid-treatment.",
    },
    {
        icon: ArrowClockwise,
        title: "Snap-back precision",
        stat: "Elastic",
        statLabel: "recovery",
        desc: "Superior elastic recovery — each aligner returns to its intended shape after insertion, maintaining a precise, snug fit from first wear to last.",
    },
    {
        icon: Diamond,
        title: "Crystal clear",
        stat: "Invisible",
        statLabel: "during wear",
        desc: "Ultra-clear material that's virtually invisible during wear, so you can straighten your smile with complete confidence.",
    },
    {
        icon: Stack,
        title: "Multi-layer engineering",
        stat: "Tri-layer",
        statLabel: "sheet construction",
        desc: "A tri-layer polymer architecture — soft inner surface for gum comfort, rigid middle layer for force delivery, polished outer skin for clarity. Three jobs, one tray.",
    },
];

const COMPARISON = [
    { feature: "Nearly invisible",                         braces: false, diy: true,  inv: true },
    { feature: "Doctor-supervised every step",             braces: true,  diy: false, inv: true },
    { feature: "AI-assisted treatment plan",               braces: false, diy: true,  inv: true },
    { feature: "Designed for Indian anatomy",              braces: false, diy: false, inv: true },
    // Material-science rows — extend the table into a continuation of the
    // tri-layer story directly above. braces=null because traditional
    // braces have no aligner material — the cell renders as "—".
    { feature: "Tri-layer aligner material",               braces: null,  diy: false, inv: true },
    { feature: "Up to 40% lower initial force",            braces: null,  diy: false, inv: true },
    { feature: "Up to 150% better force retention",        braces: null,  diy: false, inv: true },
    { feature: "6-12 month average treatment",             braces: false, diy: true,  inv: true },
    { feature: "Lifetime digital archive (Infinite™)",     braces: false, diy: false, inv: true },
    { feature: "Made-in-India quality control",            braces: false, diy: false, inv: true },
];

const EVIDENCE = [
    { stat: "96%",  label: "Patient satisfaction after 12 months" },
    { stat: "2M+",  label: "Cases analyzed by SmartForce™ AI" },
    { stat: "500+", label: "Certified provider clinics" },
    { stat: "40%",  label: "Fewer refinements vs. generic aligners" },
];

/* iter100.206 — TEAM + CERTIFICATIONS are now admin-manageable via
   /admin/landing-page → Why Invisique tab. Both arrays here are used ONLY
   as an offline fallback if the API call fails, and to keep the file
   readable during code review. Live data comes from:
     GET /api/public/why-invisique/leaders
     GET /api/public/why-invisique/certifications
*/
const TEAM_FALLBACK = [
    {
        name: "Dr. Ritu Malhotra",
        role: "Chief Clinical Officer",
        bio: "25+ years orthodontic practice · Former President, Indian Orthodontic Society",
        image_url: "/marketing/dr-ritu-malhotra.png",
    },
    {
        name: "Dr. Kunal Sharma",
        role: "Head of Treatment Planning",
        bio: "Stanford DDS · AAO Fellow · 15 years in digital orthodontics",
        image_url: "/marketing/dr-kunal-sharma.png",
    },
    {
        name: "Priya Narayanan, MS",
        role: "VP Engineering",
        bio: "Ex-Philips Healthcare · Led polymer material R&D",
        image_url: "/marketing/priya-narayanan.png",
    },
];

const CERTIFICATIONS_FALLBACK = [
    { name: "ISO 13485", sub: "Medical devices QMS" },
    { name: "CDSCO",     sub: "Govt. of India registered" },
    { name: "CE Marked", sub: "European conformity" },
    { name: "IDA",       sub: "Indian Dental Association" },
    { name: "FDA 510(k)", sub: "Filing in progress" },
];


// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

export default function WhyInvisique() {
    return (
        <div data-testid="why-invisique-page" className="overflow-x-hidden">
            <Hero />
            <EvidenceBand />
            <PillarsAtAGlance />
            <MaterialScience />
            <CoeExplainer />
            <ComparisonTable />
            <TestimonialSpotlight />
            <Team />
            <Certifications />
            <FinalCta />
        </div>
    );
}


// -----------------------------------------------------------------------------
// 1) Hero — editorial, page-specific (homepage-only signals removed
//    on 2026-02-24 user feedback: the Live counter, IDA badge, 4.9★ row,
//    dual CTAs and stat triplet were all generic top-of-funnel hooks that
//    didn't belong on a deep-dive "Why" page. The hero now stays focused
//    on the page's thesis: science-led, India-built. Bare overlay chip
//    "Designed in Bengaluru" reinforces the thesis without selling.
// -----------------------------------------------------------------------------
function Hero() {
    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 blue-grid-bg opacity-50 mask-fade-b pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    {/* LEFT — copy + "what you'll learn" preview */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-6"
                    >
                        <div
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-[0.22em] uppercase mb-6"
                            data-testid="hero-overline"
                        >
                            <Sparkle size={13} weight="fill" /> Clinically smarter
                        </div>

                        <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-bold leading-[1.04] tracking-tight">
                            Why Invisique™ <span className="text-primary">isn't just another clear aligner.</span>
                        </h1>

                        <p className="text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
                            Most aligner systems were designed in Western labs and shipped to India.
                            Invisique™ was built from the ground up <span className="text-foreground font-semibold">for Indian smiles, by Indian orthodontists</span> —
                            with science that resets the standard.
                        </p>

                        {/* Trust strip — 3 thesis-supporting pillars below the body
                            copy (added 2026-02-25 to match the Pricing hero pattern,
                            so every marketing-page hero is self-contained and ends
                            on tangible credibility). */}
                        <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-xl" data-testid="why-hero-trust-strip">
                            {[
                                { icon: Atom,        title: "Indian R&D",       desc: "Material + protocol developed in-country." },
                                { icon: ShieldStar,  title: "COE-validated",    desc: "Every plan reviewed by a second specialist." },
                                { icon: Certificate, title: "ISO 13485 made",   desc: "Manufactured to traceable quality standards." },
                            ].map((p) => (
                                <div
                                    key={p.title}
                                    className="flex items-start gap-2.5 px-3 py-2.5 rounded-2xl bg-card border border-border/60 hover:border-primary/30 transition-colors"
                                    data-testid={`why-hero-trust-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-primary text-white grid place-items-center shadow-sm ring-2 ring-secondary/40 shrink-0 mt-0.5">
                                        <p.icon size={14} weight="fill" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-[12px] font-bold leading-tight">{p.title}</div>
                                        <div className="text-[10.5px] text-muted-foreground leading-snug mt-0.5">{p.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                    </motion.div>

                    {/* RIGHT — lifestyle photo with a single thesis-supporting chip */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="lg:col-span-6 relative"
                    >
                        <div className="relative rounded-[28px] overflow-hidden bento-shadow">
                            <img
                                src={HERO_IMAGE}
                                alt="A patient with a confident, clear-aligner smile"
                                className="w-full h-full object-cover aspect-[4/5] lg:aspect-auto lg:max-h-[640px]"
                            />

                            {/* Single thesis-supporting chip — replaces the
                                homepage's 3 overlays. Stays on-message for
                                the page (Indian-built science). */}
                            <div
                                className="absolute bottom-5 left-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/95 backdrop-blur-md border border-white/40 shadow-lg text-[11px] font-bold tracking-wider text-primary"
                                data-testid="hero-india-chip"
                            >
                                <Sparkle size={13} weight="fill" />
                                DESIGNED &amp; BUILT IN INDIA
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 2) Evidence band — kept teal gradient with 4 stats
// -----------------------------------------------------------------------------
function EvidenceBand() {
    return (
        <section className="relative text-primary-foreground overflow-hidden" data-testid="stats-band">
            <div className="absolute inset-0 bg-gradient-to-br from-[#003E50] via-[#007C9D] to-[#005a73]" aria-hidden="true" />
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-amber-300/20 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-40 -left-32 w-[380px] h-[380px] rounded-full bg-cyan-300/15 blur-3xl" aria-hidden="true" />
            <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)",
                    backgroundSize: "24px 24px",
                }}
                aria-hidden="true"
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
                {EVIDENCE.map((e, i) => (
                    <motion.div
                        key={e.label}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.55, delay: i * 0.08 }}
                        data-testid={`evidence-${e.stat}`}
                        className={`relative ${i > 0 ? "md:before:absolute md:before:left-[-1rem] md:before:top-1/2 md:before:-translate-y-1/2 md:before:w-px md:before:h-12 md:before:bg-white/15" : ""}`}
                    >
                        <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight bg-gradient-to-br from-white to-cyan-100 bg-clip-text text-transparent">
                            {e.stat}
                        </div>
                        <div className="text-xs sm:text-sm text-white/80 mt-2 leading-relaxed max-w-[18ch]">
                            {e.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 3) Pillars at a Glance — compact bento grid (replaces the bulky 4-row
//    zigzag on 2026-02-24 user feedback). The deep-dive that used to live
//    inside Pillar 02 now has its own dedicated MaterialScience section
//    immediately below, where it deserves to be.
// -----------------------------------------------------------------------------
function PillarsAtAGlance() {
    return (
        <section className="py-20 lg:py-24" data-testid="pillars-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-3xl mb-10 lg:mb-12">
                    <div className="overline text-primary mb-3">The four pillars</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                        Four innovations. <span className="text-primary">One system.</span>
                    </h2>
                    <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                        Most aligners differ on one or two dimensions. Invisique™ advances all four — in parallel, for Indian patients.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {PILLARS.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.45, delay: i * 0.08 }}
                            className="group relative rounded-2xl p-6 bg-card border border-border/70 hover:border-primary/40 hover:-translate-y-0.5 transition-all"
                            data-testid={p.testid}
                        >
                            <div className="flex items-baseline justify-between mb-4">
                                <div className="w-11 h-11 rounded-xl bg-primary/10 grid place-items-center">
                                    <p.icon size={20} weight="duotone" className="text-primary" />
                                </div>
                                <span className="font-display font-bold text-primary/15 text-3xl tabular-nums leading-none">
                                    {p.num}
                                </span>
                            </div>
                            <h3 className="font-display text-base font-semibold leading-tight">
                                {p.title}
                            </h3>
                            <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                {p.blurb}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 4) Material Science — dedicated deep-dive on the premium tri-layer
//    aligner sheet. Added 2026-02-24 with the user's verbatim copy because
//    it's the page's strongest credibility beat. Structure:
//      - 2-col intro (copy left · polymer render right)
//      - 5-card feature grid (the 5 science benefits)
//      - "Why material quality matters" closer + pull-quote
// -----------------------------------------------------------------------------
function MaterialScience() {
    return (
        <section
            className="relative py-24 lg:py-32 bg-gradient-to-b from-secondary/20 via-background to-secondary/30 border-y border-border/60"
            data-testid="material-section"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                {/* INTRO — 2-col copy + product render */}
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-16 lg:mb-20">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-7"
                    >
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold tracking-[0.22em] uppercase mb-6">
                            <Stack size={13} weight="fill" /> The material
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                            Engineered with <span className="text-primary">premium-grade aligner material.</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mt-6 leading-relaxed">
                            At Invisique™, every aligner is fabricated using a <span className="text-foreground font-semibold">premium tri-layer clear aligner sheet</span> — the cutting-edge material trusted by leading orthodontic providers worldwide.
                            This isn't just any plastic; it's a precisely engineered, high-performance material designed to make your treatment more comfortable, more effective, and more predictable.
                        </p>
                        <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                            Our proprietary <span className="text-foreground font-semibold">tri-layer architecture</span> features <span className="text-foreground font-semibold">dual rigid outer shells bonded to a soft elastomeric core</span> — a structure that sets Invisique™ apart from conventional single-layer alternatives.
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="lg:col-span-5"
                    >
                        <div className="relative rounded-3xl overflow-hidden bento-shadow">
                            <img
                                src={MATERIAL_HERO_IMAGE}
                                alt="Invisique™ tri-layer aligner material architecture"
                                className="w-full aspect-[4/5] object-cover"
                                loading="lazy"
                            />
                            <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-md shadow-sm text-[10px] font-bold tracking-[0.18em] uppercase text-primary">
                                <Stack size={12} weight="fill" /> Tri-layer architecture
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* FEATURE GRID — 5 science beats */}
                <div className="mb-16 lg:mb-20">
                    <div className="flex items-baseline justify-between mb-8">
                        <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight">
                            The science behind your smile.
                        </h3>
                        <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-muted-foreground hidden sm:inline">
                            6 benefits
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {MATERIAL_FEATURES.map((f, i) => (
                            <motion.div
                                key={f.title}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.45, delay: i * 0.07 }}
                                className="rounded-2xl p-6 bg-card border border-border/70 hover:border-primary/40 transition-all"
                                data-testid={`material-feature-${i + 1}`}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-11 h-11 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                                        <f.icon size={20} weight="duotone" className="text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-display text-xl font-bold text-primary leading-none tabular-nums">
                                            {f.stat}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mt-1">
                                            {f.statLabel}
                                        </div>
                                    </div>
                                </div>
                                <h4 className="font-display text-base font-semibold leading-tight">
                                    {f.title}
                                </h4>
                                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                    {f.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* WHY IT MATTERS + pull-quote */}
                <div className="grid lg:grid-cols-12 gap-10 items-center" data-testid="material-quote-block">
                    <div className="lg:col-span-6">
                        <div className="overline text-primary mb-3">Why material quality matters</div>
                        <p className="text-base text-foreground leading-relaxed">
                            Many aligner brands cut costs by using <span className="font-semibold">single-layer materials</span> that lose force rapidly and cause unnecessary discomfort.
                            At Invisique™, we believe the material your aligner is made from <span className="font-semibold">directly impacts your results</span> — which is why we invest in only the highest-grade fabrication materials available.
                        </p>
                    </div>
                    <div className="lg:col-span-6 relative">
                        <Quotes size={36} weight="fill" className="text-primary/30 mb-2" />
                        <p className="font-display text-xl sm:text-2xl leading-snug tracking-tight font-medium">
                            "Every Invisique™ tray is precision-fabricated using
                            <span className="text-primary"> advanced tri-layer sheet material</span> — designed for
                            <span className="text-primary"> comfort on day one </span>
                            and consistent results throughout."
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 4) Testimonial spotlight — editorial dark card with credentials strip.
//    Replaces the older plain 2-col layout that felt too basic for a CCO quote.
// -----------------------------------------------------------------------------
function TestimonialSpotlight() {
    const cred = [
        { value: "25+", label: "Years in practice" },
        { value: "2,000+", label: "Cases personally reviewed" },
        { value: "Past President", label: "Indian Orthodontic Society" },
    ];
    return (
        <section
            className="relative py-24 lg:py-32 overflow-hidden"
            data-testid="testimonial-quote-block"
        >
            {/* Dark editorial backdrop — rhymes with the EvidenceBand teal but pushed
                deeper so the page has a clear visual climax before the team grid. */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-[#001f29] via-[#003E50] to-[#00111a]"
                aria-hidden="true"
            />
            {/* Decorative dot grid + soft teal glows */}
            <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)",
                    backgroundSize: "28px 28px",
                }}
                aria-hidden="true"
            />
            <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-cyan-300/10 blur-3xl" aria-hidden="true" />
            <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] rounded-full bg-amber-300/10 blur-3xl" aria-hidden="true" />

            <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
                {/* Section overline */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-14"
                >
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-cyan-100 text-[10px] font-bold tracking-[0.22em] uppercase backdrop-blur-sm border border-white/15">
                        <Sparkle size={11} weight="fill" /> A word from the Chief Clinical Officer
                    </div>
                </motion.div>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    {/* LEFT — portrait with curved teal halo + stacked accent shapes */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.94 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-5 relative flex justify-center lg:justify-start"
                    >
                        <div className="relative w-full max-w-[340px]">
                            {/* Accent square behind */}
                            <div
                                className="absolute -bottom-5 -left-5 w-32 h-32 rounded-2xl bg-primary/25 border border-primary/40"
                                aria-hidden="true"
                            />
                            {/* Teal ring offset */}
                            <div
                                className="absolute -top-4 -right-4 w-full h-full rounded-[36px] border-2 border-cyan-300/40"
                                aria-hidden="true"
                            />
                            {/* Portrait */}
                            <div className="relative rounded-[32px] overflow-hidden ring-1 ring-white/20 shadow-2xl bento-shadow">
                                <img
                                    src={TEAM_FALLBACK[0].image_url}
                                    alt={TEAM_FALLBACK[0].name}
                                    className="w-full aspect-[4/5] object-cover"
                                />
                                {/* Subtle bottom-gradient for legibility of the floating badge */}
                                <div
                                    className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent"
                                    aria-hidden="true"
                                />
                                {/* Floating identity badge */}
                                <div className="absolute bottom-4 left-4 right-4">
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur-md border border-white/40 shadow-lg">
                                        <ShieldStar size={14} weight="fill" className="text-primary" />
                                        <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-primary">
                                            Chief Clinical Officer
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* RIGHT — quote + byline + credentials strip */}
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="lg:col-span-7 relative"
                    >
                        {/* Decorative massive quote glyph */}
                        <Quotes
                            size={160}
                            weight="fill"
                            className="absolute -top-10 -left-4 text-cyan-300/10 pointer-events-none select-none hidden sm:block"
                            aria-hidden="true"
                        />

                        <p className="relative font-display text-2xl sm:text-3xl lg:text-[2.15rem] leading-[1.25] tracking-tight font-medium text-white">
                            "In 25 years of practice I've reviewed every aligner system from
                            <span className="text-cyan-300"> India and abroad</span>. Invisique™ is the first one I'd recommend to
                            <span className="text-cyan-300"> my own family </span>
                            — and that's a higher bar than any certification."
                        </p>

                        {/* Author */}
                        <div className="mt-8 flex items-center gap-3">
                            <div className="w-10 h-px bg-cyan-300/60" aria-hidden="true" />
                            <div>
                                <div className="font-semibold text-base text-white">{TEAM_FALLBACK[0].name}</div>
                                <div className="text-sm text-cyan-200/90">{TEAM_FALLBACK[0].role}, Invisique™</div>
                            </div>
                        </div>

                        {/* Credentials strip — turns the testimonial into a trust seal */}
                        <div
                            className="mt-8 grid grid-cols-3 gap-4 pt-6 border-t border-white/10"
                            data-testid="testimonial-credentials"
                        >
                            {cred.map((c) => (
                                <div key={c.label}>
                                    <div className="font-display text-xl sm:text-2xl font-bold tracking-tight text-white tabular-nums leading-none">
                                        {c.value}
                                    </div>
                                    <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-cyan-200/70 font-semibold mt-2 leading-snug">
                                        {c.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 5) Comparison table — Invisique column elevated
// -----------------------------------------------------------------------------
function ComparisonTable() {
    return (
        <section className="py-24 lg:py-32" data-testid="comparison-section">
            <div className="max-w-5xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-14">
                    <div className="overline text-primary mb-4">How we stack up</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                        The clearest choice. <span className="text-primary">Literally.</span>
                    </h2>
                    <p className="text-muted-foreground mt-5 max-w-2xl mx-auto">
                        Compared head-to-head against traditional braces and DIY aligners on the seven things that actually matter.
                    </p>
                </div>

                {/* Grid header */}
                <div className="grid grid-cols-[1.6fr_1fr_1fr_1.15fr] gap-3 mb-3 px-2">
                    <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground py-3">Feature</div>
                    <ColumnHeader label="Traditional braces" testid="comparison-table-braces" />
                    <ColumnHeader label="DIY aligners" testid="comparison-table-diy" />
                    <ColumnHeader label="Invisique™" testid="comparison-table-invisique" highlight />
                </div>

                {/* Rows */}
                <div className="space-y-1.5">
                    {COMPARISON.map((row, i) => (
                        <motion.div
                            key={row.feature}
                            initial={{ opacity: 0, x: -6 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            className="grid grid-cols-[1.6fr_1fr_1fr_1.15fr] gap-3 items-center"
                        >
                            <div className="px-2 py-3 text-sm font-medium text-foreground/90">{row.feature}</div>
                            <Cell yes={row.braces} />
                            <Cell yes={row.diy} />
                            <Cell yes={row.inv} highlight />
                        </motion.div>
                    ))}
                </div>

                <p className="text-xs text-muted-foreground text-center mt-8">
                    Based on aggregated Indian patient outcomes (2023–2025). Refinement rates vary by case complexity.
                </p>
            </div>
        </section>
    );
}

function ColumnHeader({ label, highlight, testid }) {
    if (highlight) {
        return (
            <div
                className="bg-primary text-white text-center text-xs tracking-[0.16em] uppercase font-bold py-3 px-2 rounded-t-2xl shadow-lg ring-1 ring-primary/20"
                data-testid={testid}
            >
                {label}
            </div>
        );
    }
    return (
        <div
            className="text-center text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground py-3 bg-secondary/40 rounded-t-2xl"
            data-testid={testid}
        >
            {label}
        </div>
    );
}

function Cell({ yes, highlight }) {
    if (yes === null) {
        return (
            <div className={`flex items-center justify-center py-3 text-muted-foreground/50 text-sm ${highlight ? "bg-primary/[0.06] border-x border-primary/20 ring-1 ring-primary/10" : "bg-secondary/30"}`}>
                —
            </div>
        );
    }
    if (highlight) {
        return (
            <div className="flex items-center justify-center bg-primary/[0.06] border-x border-primary/20 py-3 ring-1 ring-primary/10">
                {yes
                    ? <CheckCircle size={22} weight="fill" className="text-primary drop-shadow-[0_0_8px_rgba(0,124,157,0.4)]" />
                    : <XCircle size={22} className="text-muted-foreground/40" />
                }
            </div>
        );
    }
    return (
        <div className="flex items-center justify-center bg-secondary/30 py-3">
            {yes
                ? <CheckCircle size={20} weight="fill" className="text-foreground/55" />
                : <XCircle size={20} className="text-muted-foreground/35" />
            }
        </div>
    );
}


// -----------------------------------------------------------------------------
// 6) Team — staggered 3-card collage
// -----------------------------------------------------------------------------
function Team() {
    const [team, setTeam] = useState(TEAM_FALLBACK);
    useEffect(() => {
        api.get("/public/why-invisique/leaders")
            .then((r) => {
                const items = r.data?.items || [];
                if (items.length) setTeam(items);
            })
            .catch(() => { /* fallback stays */ });
    }, []);
    return (
        <section className="py-24 lg:py-32 bg-secondary/30 border-y border-border/60" data-testid="team-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="max-w-2xl mb-14">
                    <div className="overline text-primary mb-4">Built by leaders</div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                        Scientific advisory <span className="text-primary">you can trust.</span>
                    </h2>
                    <p className="text-lg text-muted-foreground mt-5 leading-relaxed">
                        Decades of clinical practice and medical-device engineering — every aligner you receive carries their fingerprint.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
                    {team.map((m, i) => {
                        // Stagger middle card only when exactly 3 members;
                        // for flexible 1–6 counts we keep the row flat.
                        const stagger = team.length === 3 && i === 1;
                        // Backwards-compat: seed rows use image_url; fallback array also uses image_url.
                        const src = m.image_url || m.image || "";
                        const slug = (m.name || "").toLowerCase().replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "");
                        return (
                            <motion.div
                                key={m.id || m.name || i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.12 }}
                                className={`group ${stagger ? "md:mt-12" : ""}`}
                                data-testid={`team-card-${slug}`}
                            >
                                <div className="relative rounded-3xl overflow-hidden bento-shadow bg-card">
                                    <div className="aspect-[4/5] overflow-hidden">
                                        {src ? (
                                            <img
                                                src={src}
                                                alt={m.name}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="w-full h-full grid place-items-center bg-primary/5 text-primary/40">
                                                <UsersThree size={48} weight="duotone" />
                                            </div>
                                        )}
                                    </div>
                                    {/* Glassmorphic name strip — slides up subtly on hover */}
                                    <div className="absolute bottom-3 left-3 right-3 rounded-2xl bg-white/85 backdrop-blur-xl border border-white/60 p-4 shadow-lg transition-all duration-500 group-hover:bottom-4">
                                        <div className="font-display text-lg font-semibold leading-tight">{m.name}</div>
                                        <div className="text-xs text-primary font-bold tracking-wider uppercase mt-0.5">{m.role}</div>
                                        <p className="text-[11px] text-muted-foreground mt-2 leading-snug">{m.bio}</p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 7) Certifications — outlined cards, not flat text
// -----------------------------------------------------------------------------
function Certifications() {
    const [certs, setCerts] = useState(CERTIFICATIONS_FALLBACK);
    useEffect(() => {
        api.get("/public/why-invisique/certifications")
            .then((r) => {
                const items = r.data?.items || [];
                if (items.length) setCerts(items);
            })
            .catch(() => { /* fallback stays */ });
    }, []);
    return (
        <section className="py-20 lg:py-24" data-testid="certifications-row">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="text-center mb-10">
                    <div className="overline text-muted-foreground mb-3 tracking-[0.22em]">Trusted & certified by</div>
                    <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Held to the world's highest standards.
                    </h3>
                </div>

                <div className="flex flex-wrap justify-center gap-3">
                    {certs.map((c, i) => (
                        <motion.div
                            key={c.id || c.name || i}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            data-testid={`cert-${(c.name || "").toLowerCase().replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "")}`}
                            className="flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-card border-2 border-border/70 hover:border-primary/40 hover:-translate-y-0.5 transition-all"
                        >
                            <div className="w-9 h-9 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                                <Certificate size={18} weight="duotone" className="text-primary" />
                            </div>
                            <div className="text-left">
                                <div className="font-semibold text-sm leading-tight">{c.name}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">{c.sub}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// 8) Final CTA — premium conviction moment at the bottom of a long page.
//    2026-02-28 redesign: diverged from the Landing-page CTA per user request.
//    Landing is top-of-funnel (social-proof in the middle); this WhyInvisique CTA
//    is the FINAL conversion moment — gets a heavier premium dark-teal hero card
//    with a trust-pills row underneath. Visual weight matches its conversion
//    importance.
// -----------------------------------------------------------------------------
function FinalCta() {
    const TRUST_PILLS = [
        { icon: MapPin,      label: "12+ cities" },
        { icon: Certificate, label: "IDA certified" },
        { icon: ShieldStar,  label: "4.9★ rated" },
        // iter100.172 — Replaced the redundant "Free consult" pill (already
        // covered by the paragraph copy above) with a differentiating
        // product feature. AI Smile Preview is unique to Invisique and
        // reinforces the tech-forward positioning of the WhyInvisique page.
        { icon: Sparkle,     label: "AI Smile Preview" },
    ];
    return (
        <section className="pb-24 lg:pb-32" data-testid="final-cta-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-primary/85 text-white p-8 lg:p-14 bento-shadow"
                    data-testid="why-final-cta"
                >
                    {/* Decorative blue-grid + subtle glow halo — reuses the marketing
                        hero treatment so this card feels premium and on-brand without
                        a new texture. */}
                    <div className="absolute inset-0 blue-grid-bg opacity-10 mask-fade-b pointer-events-none" aria-hidden="true" />
                    <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" aria-hidden="true" />
                    <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none" aria-hidden="true" />

                    {/* TOP ROW — heading + CTA */}
                    <div className="relative flex flex-col lg:flex-row items-center lg:items-end justify-between gap-7">
                        <div className="text-center lg:text-left max-w-2xl">
                            <div className="overline text-white/60 mb-3">Ready when you are</div>
                            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                                Your smile journey starts<br />with <span className="text-amber-300">one click.</span>
                            </h3>
                            <p className="text-white/85 mt-4 text-sm sm:text-base max-w-md mx-auto lg:mx-0">
                                No pressure. No commitment. Just a free consultation with a certified doctor near you.
                            </p>
                        </div>
                        <Link to="/find-doctor" className="relative group shrink-0" data-testid="final-cta-primary-link">
                            <span className="absolute inset-0 rounded-full bg-amber-400/50 blur-lg animate-pulse pointer-events-none" aria-hidden="true" />
                            <Button
                                size="lg"
                                className="relative rounded-full h-14 px-9 bg-amber-400 hover:bg-amber-300 text-foreground font-bold tracking-wide transition-all group-hover:scale-[1.04] shadow-xl"
                                data-testid="final-cta-primary"
                            >
                                <MapPin size={17} weight="fill" className="mr-2" />
                                FIND A DOCTOR
                                <ArrowRight size={15} weight="bold" className="ml-2 group-hover:translate-x-0.5 transition-transform" />
                            </Button>
                        </Link>
                    </div>

                    {/* BOTTOM ROW — trust pills strip. Splits the card vertically so
                        the conversion ask (top) is the hero, and the credibility
                        reminders (bottom) close any last-mile objections. */}
                    <div className="relative mt-8 lg:mt-12 pt-6 lg:pt-8 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="final-cta-trust-strip">
                        {TRUST_PILLS.map((p, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-center lg:justify-start gap-2 text-white/85 text-xs sm:text-sm font-semibold"
                                data-testid={`final-cta-trust-${i}`}
                            >
                                <p.icon size={16} weight="fill" className="text-amber-300 shrink-0" />
                                <span>{p.label}</span>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}


// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------
function Stat({ value, label }) {
    return (
        <div>
            <div className="font-display text-2xl font-bold text-foreground tabular-nums leading-none">{value}</div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold mt-1.5">{label}</div>
        </div>
    );
}

function Divider() {
    return <span className="hidden sm:inline-block w-px h-8 bg-border" aria-hidden="true" />;
}


// -----------------------------------------------------------------------------
// CoeExplainer (2026-02-25) — turns the abstract "Center of Excellence" term
// (mentioned 6+ times across the site as the source of "COE-validated" trust
// pills) into a tangible explainer band. Sits between MaterialScience and
// ComparisonTable on Why-Invisique — directly answers "what IS the COE?"
// before the page moves into competitive positioning.
// -----------------------------------------------------------------------------
const COE_PILLARS = [
    {
        icon: UsersThree,
        title: "Second-specialist review",
        desc: "Every treatment plan is reviewed by an Orthodontist from our COE panel before manufacturing begins.",
    },
    {
        icon: MagnifyingGlass,
        title: "Edge-case escalation",
        desc: "Complex skeletal cases, attachment placements, or biomechanical conflicts are flagged and re-engineered, not just rubber-stamped.",
    },
    {
        icon: Note,
        title: "Treatment-plan audit",
        desc: "Stage-by-stage movement analysis ensures the plan respects bone, gum, and root anatomy at every aligner.",
    },
];

const COE_STATS = [
    { value: "100%",     label: "Plans reviewed independently" },
    { value: "< 48 hrs", label: "Average plan-review SLA" },
    { value: "9 yrs",    label: "Average specialist experience" },
];

function CoeExplainer() {
    return (
        <section className="relative py-20 lg:py-28 bg-secondary/30 border-y border-border/60 overflow-hidden" data-testid="coe-explainer-section">
            {/* Soft backdrop accents */}
            <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-primary/8 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-amber-200/25 blur-3xl pointer-events-none" aria-hidden="true" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                {/* Section header — same overline + H2 + supporting paragraph pattern */}
                <div className="max-w-3xl mb-12 lg:mb-14">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-bold tracking-[0.22em] uppercase mb-4">
                        <ShieldStar size={11} weight="fill" /> Inside the Center of Excellence
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                        Before your plan is finalised, a second set of expert eyes <span className="text-primary">reviews it as standard.</span>
                    </h2>
                    <p className="text-muted-foreground mt-4 text-base lg:text-lg leading-relaxed">
                        Our Center of Excellence Team reviews every plan, every aligner, every retainer.
                        Here's what it actually means — and why we made it the non-negotiable centre of how Invisique™ works.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
                    {/* LEFT — 3 pillar cards (vertical stack on desktop, single column on mobile) */}
                    <div className="lg:col-span-7 space-y-4">
                        {COE_PILLARS.map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 14 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.45, delay: i * 0.06 }}
                                className="relative bg-card border border-border/70 rounded-2xl p-5 lg:p-6 bento-shadow hover:border-primary/40 hover:-translate-y-0.5 transition-all"
                                data-testid={`coe-pillar-${i}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-full bg-primary text-white grid place-items-center shadow-md ring-4 ring-secondary/30 shrink-0">
                                        <p.icon size={20} weight="fill" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-display text-base lg:text-lg font-bold leading-tight">{p.title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed mt-1.5">{p.desc}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* RIGHT — stat card with the COE numbers */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="lg:col-span-5 relative rounded-3xl overflow-hidden bento-shadow"
                        data-testid="coe-stat-card"
                    >
                        {/* Dark teal editorial card to make the numbers pop */}
                        <div className="absolute inset-0 bg-gradient-to-br from-[#001f29] via-[#003E50] to-[#00111a]" aria-hidden="true" />
                        <div
                            className="absolute inset-0 opacity-[0.08]"
                            style={{
                                backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.85) 1px, transparent 1px)",
                                backgroundSize: "26px 26px",
                            }}
                            aria-hidden="true"
                        />
                        <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-amber-300/15 blur-3xl" aria-hidden="true" />
                        <div className="absolute -bottom-24 -left-16 w-48 h-48 rounded-full bg-cyan-300/15 blur-3xl" aria-hidden="true" />

                        <div className="relative p-7 lg:p-8 text-white h-full flex flex-col">
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-cyan-100 text-[10px] font-bold tracking-[0.22em] uppercase backdrop-blur-sm border border-white/15 self-start mb-6">
                                <Sparkle size={11} weight="fill" /> The COE in numbers
                            </div>

                            <div className="space-y-7 flex-1 flex flex-col justify-center">
                                {COE_STATS.map((s) => (
                                    <div key={s.label} className="flex items-baseline justify-between gap-4 pb-5 border-b border-white/10 last:border-b-0 last:pb-0">
                                        <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-br from-white to-cyan-100 bg-clip-text text-transparent tabular-nums leading-none">
                                            {s.value}
                                        </div>
                                        <div className="text-[11px] sm:text-xs uppercase tracking-[0.16em] text-cyan-100/80 font-semibold text-right max-w-[55%] leading-snug">
                                            {s.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-7 pt-5 border-t border-white/10 text-[12.5px] text-cyan-100/85 italic leading-relaxed font-display">
                                Same standard. Every doctor. Every clinic.
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}




// Re-exports for parity with the old file (callers don't need them but keep
// the named exports stable for future swaps).
// Named re-exports for parity with the old file (callers don't need them
// but keep stable for future swaps). PillarsAtAGlance + MaterialScience
// replace the retired Pillars on 2026-02-24. CoeExplainer added 2026-02-25.
export { Hero, PillarsAtAGlance, MaterialScience, CoeExplainer, ComparisonTable, Team, Certifications, FinalCta };
